import { Box, Paper, Title, Text, Button, Flex, Container, Anchor, ThemeIcon, Image } from "@mantine/core";
import { IconMail, IconRefresh, IconArrowLeft } from "@tabler/icons-react";
import { Link, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import trpc from "../../shared/utils/trpc";
import { successNotification } from "../../shared/utils/successNotification";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";

function ResendEmailPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(120);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email || cooldown > 0) return;

    setLoading(true);
    try {
      await trpc.auth.resendVerification.mutate({ email });

      successNotification({
        title: "Verification Email Sent",
        message: "Please check your inbox and spam folder.",
      });

      // Start 15 second cooldown
      setCooldown(120);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        {/* Header */}
        <Flex direction="column" align="center" gap={4} mb="xl">
          <Image src="/gtLogo.svg" alt="GTsetu" h={25} w={170} fit="contain" />
        </Flex>

        {/* Card */}
        <Paper
          shadow="sm"
          p={{ base: "lg", sm: "xl" }}
          radius="lg"
          maw={550}
          mx="auto"
          style={{ border: "1px solid #E5E7EB" }}
        >
          {/* Mail Icon */}
          <Flex justify="center" mb="lg">
            <ThemeIcon
              size={60}
              radius="xl"
              variant="light"
              color="primary"
              styles={{
                root: {
                  backgroundColor: "#DBEAFE",
                },
              }}
            >
              <IconMail size={32} color="#2563EB" />
            </ThemeIcon>
          </Flex>

          {/* Title */}
          <Title order={2} fz={{ base: 20, sm: 24 }} ta="center" mb="sm">
            Verify Your Email Address
          </Title>

          {/* Description */}
          <Text fz="sm" c="slate.6" ta="center" mb="xl">
            We've sent a verification link to your registered email address.
          </Text>

          {/* Email Box */}
          <Box bg="#F1F5F9" p="md" mb="md" style={{ borderRadius: "8px" }}>
            <Text fz="sm" ta="center" fw={500} c="#334155">
              {email}
            </Text>
            <Text fz="xs" c="slate.6" ta="center" mt="xs">
              Please check your inbox and click the link to continue.
            </Text>
          </Box>

          {/* Resend Button */}
          <Button
            fullWidth
            size="md"
            leftSection={<IconRefresh size={18} />}
            onClick={handleResend}
            loading={loading}
            disabled={cooldown > 0 || loading}
          >
            {cooldown > 0 ? `Resend Verification Email (${formatTime(cooldown)})` : "Resend Verification Email"}
          </Button>

          {/* Helper Text */}
          <Text fz="xs" c="slate.6" ta="center" mt="md">
            You can resend the email if you didn't receive it.
          </Text>

          {/* Change Email Link */}
          <Flex justify="center" mt="md">
            <Anchor component={Link} to="/login" size="sm" c="#2563EB">
              <Flex align="center" gap={4}>
                <IconArrowLeft size={14} />
                Back to login
              </Flex>
            </Anchor>
          </Flex>
        </Paper>
      </Container>
    </Box>
  );
}

export default ResendEmailPage;
